import { useRef, useState, type CSSProperties } from "react";
import {
  closestCenter,
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  IconDotsVertical,
  IconEye,
  IconEyeOff,
  IconGripVertical,
  IconPencil,
  IconPhoto,
  IconTrash,
  IconVideo,
} from "@tabler/icons-react";
import { paths } from "@/App";
import { DfyApi } from "@/lib/api/dfy-api";
import {
  dfyQueryKeys,
  useDfyEditorOrderQuery,
} from "@/lib/queries/dfy-queries";
import { Button } from "@/lib/components/Button/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/lib/components/Select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import { Input } from "@/lib/components/Input";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import EmptyState from "@/lib/components/EmptyState";
import Toast from "@/lib/components/Toast";
import { DfyOrderStatus } from "@repo/api/dfy";
import type {
  CreateVideoFromDfyOrderResponseDto,
  DfyDeliveryDto,
  DfyOrderWithAssetsDto,
  UpdateDfyEditorOrderDto,
} from "@repo/api/dfy";
import { DfyOrderStatusDot, dfyStatusLabel } from "./DfyOrderStatusBadge";
import { Switch } from "@/lib/components/Switch";
import { Badge } from "@/lib/components/Badge";
import RenderPickerDialog from "./RenderPickerDialog";
import DfyOrderDetailsLayout, {
  DfyDeliveryCard,
  type DfyOrderDetailsTab,
} from "./DfyOrderDetailsLayout";

const EDITOR_STATUS_OPTIONS: DfyOrderStatus[] = [
  "SUBMITTED",
  "IN_PROGRESS",
  "DELIVERED",
];

export default function DfyEditorOrderDetailsPage() {
  const { teamExternalId, orderExternalId } = useParams();
  return <DfyEditorOrderDetails key={`${teamExternalId}:${orderExternalId}`} />;
}

function DfyEditorOrderDetails() {
  const { teamExternalId, orderExternalId } = useParams();
  const queryClient = useQueryClient();
  const [isRenderPickerOpen, setIsRenderPickerOpen] = useState(false);
  const [isDeliverConfirmOpen, setIsDeliverConfirmOpen] = useState(false);
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [tab, setTab] = useState<DfyOrderDetailsTab>("deliveries");

  const queryKey = dfyQueryKeys.editorOrder(teamExternalId, orderExternalId);

  const { data, isLoading } = useDfyEditorOrderQuery(
    teamExternalId,
    orderExternalId,
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const statusMutation = useMutation({
    mutationFn: (dto: UpdateDfyEditorOrderDto) =>
      DfyApi.updateEditorOrder(teamExternalId!, orderExternalId!, dto),
    onSuccess: invalidate,
    onError: () => Toast.error("Failed to update status"),
  });

  const createVideoMutation = useMutation<
    CreateVideoFromDfyOrderResponseDto,
    Error,
    Window | null
  >({
    mutationFn: () => DfyApi.createVideo(teamExternalId!, orderExternalId!),
    onSuccess: (result, newTab) => {
      invalidate();
      queryClient.invalidateQueries({
        queryKey: ["dfy", "editor-orders", teamExternalId],
      });
      const videoPath = paths.videoEdit(
        teamExternalId!,
        result.videoExternalId,
      );
      if (newTab) {
        newTab.location.href = videoPath;
      } else {
        window.open(videoPath, "_blank");
      }
    },
    onError: (_error, newTab) => {
      newTab?.close();
      Toast.error("Failed to create video");
    },
  });

  // Browsers block window.open outside a click handler, so the tab is opened
  // before the video is created and navigated once the API call resolves.
  const handleCreateVideo = () => {
    createVideoMutation.mutate(window.open("", "_blank"));
  };

  const toggleDeliveryMutation = useMutation({
    mutationFn: ({
      deliveryExternalId,
      isVisibleForCustomer,
    }: {
      deliveryExternalId: string;
      isVisibleForCustomer: boolean;
    }) =>
      DfyApi.updateDelivery(teamExternalId!, deliveryExternalId, {
        isVisibleForCustomer,
      }),
    onMutate: async ({ deliveryExternalId, isVisibleForCustomer }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<DfyOrderWithAssetsDto>(queryKey);
      if (previous) {
        queryClient.setQueryData<DfyOrderWithAssetsDto>(queryKey, {
          ...previous,
          deliveries: previous.deliveries.map((delivery) =>
            delivery.externalId === deliveryExternalId
              ? { ...delivery, isVisibleForCustomer }
              : delivery,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      Toast.error("Failed to update delivery");
    },
  });

  const renameDeliveryMutation = useMutation({
    mutationFn: ({
      deliveryExternalId,
      name,
    }: {
      deliveryExternalId: string;
      name: string;
    }) => DfyApi.updateDelivery(teamExternalId!, deliveryExternalId, { name }),
    onMutate: async ({ deliveryExternalId, name }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<DfyOrderWithAssetsDto>(queryKey);
      if (previous) {
        queryClient.setQueryData<DfyOrderWithAssetsDto>(queryKey, {
          ...previous,
          deliveries: previous.deliveries.map((delivery) =>
            delivery.externalId === deliveryExternalId
              ? { ...delivery, name }
              : delivery,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      Toast.error("Failed to rename delivery");
    },
  });

  const thumbnailMutation = useMutation({
    mutationFn: ({
      deliveryExternalId,
      thumbnailTimeMs,
    }: {
      deliveryExternalId: string;
      thumbnailTimeMs: number;
    }) =>
      DfyApi.updateDelivery(teamExternalId!, deliveryExternalId, {
        thumbnailTimeMs,
      }),
    onSuccess: invalidate,
    onError: () => Toast.error("Failed to update thumbnail"),
  });

  const reorderDeliveriesMutation = useMutation({
    mutationFn: (deliveries: DfyDeliveryDto[]) =>
      DfyApi.updateEditorOrder(teamExternalId!, orderExternalId!, {
        deliverySortOrder: deliveries.map((delivery) => delivery.externalId),
      }),
    onMutate: async (deliveries) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<DfyOrderWithAssetsDto>(queryKey);
      if (previous) {
        queryClient.setQueryData<DfyOrderWithAssetsDto>(queryKey, {
          ...previous,
          deliveries,
        });
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      Toast.error("Failed to reorder deliveries");
    },
  });

  const deleteDeliveryMutation = useMutation({
    mutationFn: (deliveryExternalId: string) =>
      DfyApi.deleteDelivery(teamExternalId!, deliveryExternalId),
    onMutate: async (deliveryExternalId) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<DfyOrderWithAssetsDto>(queryKey);
      if (previous) {
        queryClient.setQueryData<DfyOrderWithAssetsDto>(queryKey, {
          ...previous,
          deliveries: previous.deliveries.filter(
            (delivery) => delivery.externalId !== deliveryExternalId,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      Toast.error("Failed to delete delivery");
    },
  });

  if (!teamExternalId || !orderExternalId) {
    return null;
  }

  if (isLoading || !data) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const { order, submittedByEmail, deliveries, sourceImages, assetMedias } =
    data;

  return (
    <DfyOrderDetailsLayout
      teamExternalId={teamExternalId}
      order={order}
      submittedByEmail={submittedByEmail}
      sourceImages={sourceImages}
      assetMedias={assetMedias}
      backTo={paths.dfyEditorOrders(teamExternalId)}
      tab={tab}
      onTabChange={setTab}
      actions={
        <>
          <Select
            value={order.status}
            onValueChange={(status) => {
              if (status === "DELIVERED") {
                setNotifyCustomer(true);
                setIsDeliverConfirmOpen(true);
              } else {
                statusMutation.mutate({ status: status as DfyOrderStatus });
              }
            }}
            disabled={statusMutation.isPending}
          >
            <SelectTrigger
              className="w-auto min-w-37 gap-2 pl-4"
              aria-label={`Order status: ${dfyStatusLabel(order.status)}`}
              aria-busy={statusMutation.isPending}
            >
              <SelectValue>
                <span className="flex items-center gap-2">
                  <DfyOrderStatusDot status={order.status} />
                  {dfyStatusLabel(order.status)}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent align="end">
              {EDITOR_STATUS_OPTIONS.map((status) => (
                <SelectItem key={status} value={status}>
                  <span className="flex items-center gap-2">
                    <DfyOrderStatusDot status={status} />
                    {dfyStatusLabel(status)}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            isLoading={createVideoMutation.isPending}
            disabled={createVideoMutation.isPending}
            onClick={handleCreateVideo}
          >
            Create video
          </Button>
          <Button
            iconPosition="leading"
            onClick={() => setIsRenderPickerOpen(true)}
          >
            Add render
          </Button>
        </>
      }
      deliveriesTab={
        <DfyEditorOrderVideosTab
          deliveries={deliveries}
          isCreatingVideo={createVideoMutation.isPending}
          onCreateVideo={handleCreateVideo}
          onAddRender={() => setIsRenderPickerOpen(true)}
          onReorderDeliveries={(reordered) =>
            reorderDeliveriesMutation.mutate(reordered)
          }
          onToggleDelivery={(delivery) =>
            toggleDeliveryMutation.mutate(delivery)
          }
          onRenameDelivery={(delivery) =>
            renameDeliveryMutation.mutate(delivery)
          }
          onSetThumbnail={(delivery) => thumbnailMutation.mutate(delivery)}
          onDeleteDelivery={(deliveryExternalId) =>
            deleteDeliveryMutation.mutate(deliveryExternalId)
          }
        />
      }
    >
      <ConfirmDeliverDialog
        open={isDeliverConfirmOpen}
        onOpenChange={setIsDeliverConfirmOpen}
        isPending={statusMutation.isPending}
        notifyCustomer={notifyCustomer}
        onNotifyCustomerChange={setNotifyCustomer}
        onConfirm={() =>
          statusMutation.mutate(
            { status: "DELIVERED", notifyCustomer },
            { onSuccess: () => setIsDeliverConfirmOpen(false) },
          )
        }
      />
      <RenderPickerDialog
        open={isRenderPickerOpen}
        onOpenChange={setIsRenderPickerOpen}
        teamExternalId={teamExternalId}
        orderExternalId={orderExternalId}
        addedRenderExternalIds={deliveries.map(
          (delivery) => delivery.videoRender.externalId,
        )}
        onAdded={(delivery) => {
          const previous =
            queryClient.getQueryData<DfyOrderWithAssetsDto>(queryKey);
          if (previous) {
            queryClient.setQueryData<DfyOrderWithAssetsDto>(queryKey, {
              ...previous,
              deliveries: [delivery, ...previous.deliveries],
            });
          }
        }}
      />
    </DfyOrderDetailsLayout>
  );
}

function DfyEditorOrderVideosTab({
  deliveries,
  isCreatingVideo,
  onCreateVideo,
  onAddRender,
  onReorderDeliveries,
  onToggleDelivery,
  onRenameDelivery,
  onSetThumbnail,
  onDeleteDelivery,
}: {
  deliveries: DfyDeliveryDto[];
  isCreatingVideo: boolean;
  onCreateVideo: () => void;
  onAddRender: () => void;
  onReorderDeliveries: (deliveries: DfyDeliveryDto[]) => void;
  onToggleDelivery: (delivery: {
    deliveryExternalId: string;
    isVisibleForCustomer: boolean;
  }) => void;
  onRenameDelivery: (delivery: {
    deliveryExternalId: string;
    name: string;
  }) => void;
  onSetThumbnail: (delivery: {
    deliveryExternalId: string;
    thumbnailTimeMs: number;
  }) => void;
  onDeleteDelivery: (deliveryExternalId: string) => void;
}) {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 8 },
    }),
  );

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const ids = deliveries.map((delivery) => delivery.externalId);
    onReorderDeliveries(
      arrayMove(
        deliveries,
        ids.indexOf(String(active.id)),
        ids.indexOf(String(over.id)),
      ),
    );
  }

  if (deliveries.length === 0) {
    return (
      <div className="h-full min-h-80">
        <EmptyState
          icon={<IconVideo size={24} />}
          header="No renders added yet"
          subHeader="Create a video from this order, or add an existing render."
          primaryActionLabel="Add render"
          primaryActionOnClick={onAddRender}
          secondaryActionLabel={
            isCreatingVideo ? "Creating video..." : "Create video"
          }
          secondaryActionOnClick={() => {
            if (!isCreatingVideo) onCreateVideo();
          }}
        />
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={deliveries.map((delivery) => delivery.externalId)}
        strategy={rectSortingStrategy}
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {deliveries.map((delivery) => (
            <DfyEditorDeliveryCard
              key={delivery.externalId}
              delivery={delivery}
              onToggleDelivery={onToggleDelivery}
              onRenameDelivery={onRenameDelivery}
              onSetThumbnail={onSetThumbnail}
              onDeleteDelivery={onDeleteDelivery}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function DfyEditorDeliveryCard({
  delivery,
  onToggleDelivery,
  onRenameDelivery,
  onSetThumbnail,
  onDeleteDelivery,
}: {
  delivery: DfyDeliveryDto;
  onToggleDelivery: (delivery: {
    deliveryExternalId: string;
    isVisibleForCustomer: boolean;
  }) => void;
  onRenameDelivery: (delivery: {
    deliveryExternalId: string;
    name: string;
  }) => void;
  onSetThumbnail: (delivery: {
    deliveryExternalId: string;
    thumbnailTimeMs: number;
  }) => void;
  onDeleteDelivery: (deliveryExternalId: string) => void;
}) {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: delivery.externalId });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    position: "relative",
  };

  return (
    <div ref={setNodeRef} style={style}>
      <DfyDeliveryCard
        delivery={delivery}
        videoRef={videoRef}
        badges={
          !delivery.isVisibleForCustomer && (
            <Badge
              size="xs"
              variant="tertiarySubtle"
              className="shrink-0 gap-1"
            >
              <IconEyeOff size={12} />
              Hidden
            </Badge>
          )
        }
        overlay={
          <>
            <div className="pointer-events-none absolute top-0 left-0 h-16 w-16 rounded-tl-lg bg-[radial-gradient(circle_at_top_left,rgba(0,0,0,0.5),transparent_70%)]" />
            <button
              type="button"
              ref={setActivatorNodeRef}
              className="absolute top-0 left-0 cursor-grab touch-none p-2 text-white/90 active:cursor-grabbing"
              aria-label="Reorder delivery"
              {...attributes}
              {...listeners}
            >
              <IconGripVertical size={20} />
            </button>
          </>
        }
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="iconXs"
                className="shrink-0"
                aria-label="Delivery options"
              >
                <IconDotsVertical size={18} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setIsEditOpen(true)}>
                <IconPencil size={16} />
                Edit title
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  const video = videoRef.current;
                  if (!video) return;
                  video.pause();
                  onSetThumbnail({
                    deliveryExternalId: delivery.externalId,
                    thumbnailTimeMs: Math.round(video.currentTime * 1000),
                  });
                }}
              >
                <IconPhoto size={16} />
                Use current frame as thumbnail
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() =>
                  onToggleDelivery({
                    deliveryExternalId: delivery.externalId,
                    isVisibleForCustomer: !delivery.isVisibleForCustomer,
                  })
                }
              >
                {delivery.isVisibleForCustomer ? (
                  <IconEyeOff size={16} />
                ) : (
                  <IconEye size={16} />
                )}
                {delivery.isVisibleForCustomer
                  ? "Hide from customer"
                  : "Show to customer"}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-error focus:text-error"
                onSelect={() => onDeleteDelivery(delivery.externalId)}
              >
                <IconTrash size={16} />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
        footer={
          <EditDeliveryTitleDialog
            open={isEditOpen}
            onOpenChange={setIsEditOpen}
            initialName={delivery.name}
            onSave={(name) => {
              onRenameDelivery({
                deliveryExternalId: delivery.externalId,
                name,
              });
              setIsEditOpen(false);
            }}
          />
        }
      />
    </div>
  );
}

function ConfirmDeliverDialog({
  open,
  onOpenChange,
  isPending,
  notifyCustomer,
  onNotifyCustomerChange,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  notifyCustomer: boolean;
  onNotifyCustomerChange: (notifyCustomer: boolean) => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark order as delivered?</DialogTitle>
          <DialogDescription>
            {notifyCustomer
              ? "The customer will be notified in the app and by email that their order has been delivered."
              : "The customer will not be notified about the delivery."}{" "}
            Make sure the videos are ready and visible to the customer before
            continuing.
          </DialogDescription>
        </DialogHeader>
        <label className="flex items-center gap-2 text-sm font-medium">
          <Switch
            checked={notifyCustomer}
            onCheckedChange={onNotifyCustomerChange}
            disabled={isPending}
          />
          Notify customer
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            isLoading={isPending}
            disabled={isPending}
            onClick={onConfirm}
          >
            Mark as delivered
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDeliveryTitleDialog({
  open,
  onOpenChange,
  initialName,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName: string;
  onSave: (name: string) => void;
}) {
  const [name, setName] = useState(initialName);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) setName(initialName);
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit title</DialogTitle>
        </DialogHeader>
        <Input
          value={name}
          placeholder="Video title"
          autoFocus
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleSave();
          }}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!name.trim()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
